import React, { useEffect } from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';

//import stles before grid renders
import { agGridMojaveLight } from './aggrid-theme';

// There are filter modules that can be added for each column to have a filter in the grid. 
// Includes TextFilter, NumberFilter, DateFilter, SetFilter, AdvancedFilter, MultiFilter
// Currently not included because some of the grids can't send the data to the filter to make them useful.
import type {
  DefaultMenuItem,
  GetMainMenuItemsParams,
  MenuItemDef,
} from 'ag-grid-community';
import {
  ModuleRegistry,
  LicenseManager,
  MasterDetailModule,
  CellSelectionModule,
  MenuModule,
  ColumnsToolPanelModule,
  ServerSideRowModelModule,
  RowGroupingModule,
  ValidationModule,
  ColumnAutoSizeModule,
  CellStyleModule,
  RowAutoHeightModule,
  RowDragModule,
  CustomEditorModule,
  RenderApiModule,
  PaginationModule,
  provideGlobalGridOptions,
  ClientSideRowModelModule,
  RowSelectionModule,
  TooltipModule,
  ContextMenuModule,
  ClipboardModule,
  ServerSideRowModelApiModule,
  RowApiModule
} from 'ag-grid-enterprise';

// Declare the window property properly
declare global {
  interface Window {
    __CMX_GRID_INITIALIZED__?: boolean;
    BUILD_CONFIG?: { GRID_LICENSE_KEY?: string };
  }
}

// Set license before any AG Grid initialization (required for UMD / script-tag loading).
// BUILD_CONFIG is set by build-config.js which loads before this script; retry once if not yet ready.
function getLicenseKey(): string {
  return (typeof window !== 'undefined' ? window.BUILD_CONFIG?.GRID_LICENSE_KEY : undefined) ?? '';
}
function applyLicense(): void {
  const key = getLicenseKey();
  LicenseManager.setLicenseKey(key);
  if (!key && typeof window !== 'undefined') {
    requestAnimationFrame(() => LicenseManager.setLicenseKey(getLicenseKey()));
  }
}
applyLicense();

// Add initialization guard
if (window.__CMX_GRID_INITIALIZED__) {
  console.warn(
    'AG-Grid DUPLICATE grid module load detected - skipping initialization'
  );
} else {
  // Select Modules we want to use.
  // ValidationModule: dev only per AG Grid 33 upgrade guide (avoids production bundle bloat).
  const modules = [
    ...(process.env.NODE_ENV === 'production' ? [] : [ValidationModule]),
    ServerSideRowModelModule,
    ServerSideRowModelApiModule ,
    RowApiModule,
    RowGroupingModule,
    MasterDetailModule,
    CellSelectionModule,
    MenuModule,
    ColumnsToolPanelModule,
    ColumnAutoSizeModule,
    CellStyleModule,
    RowDragModule,
    CustomEditorModule,
    RowAutoHeightModule,
    RenderApiModule,
    PaginationModule,
    ClientSideRowModelModule,
    RowSelectionModule,
    TooltipModule,
    ContextMenuModule,
    ClipboardModule
    // IntegratedChartsModule + AgChartsEnterpriseModule require a combined Grid+Charts license.
    // Re-add when license covers both: IntegratedChartsModule.with(AgChartsEnterpriseModule)
  ];
  ModuleRegistry.registerModules(modules);

  // Apply theme and shared defaults (service-desk, dashboard, etc.).
  // Column menu: remove "Choose Columns", enforce consistent order (sort, then pin, then rest).
  provideGlobalGridOptions(
    {
      theme: agGridMojaveLight,
      getMainMenuItems: (params: GetMainMenuItemsParams) => {
        const nameOf = (item: MenuItemDef | DefaultMenuItem): string | undefined =>
          typeof item === 'string' ? item : (item as MenuItemDef).name;
        const byName: Record<string, MenuItemDef | DefaultMenuItem> = {};
        const itemsToExclude: DefaultMenuItem[] = ['columnChooser'];
        // When column is sortable: false, hide sort options in the menu (header click already disabled).
        const colDef = params.column?.getColDef?.();
        if (colDef?.sortable === false) {
          itemsToExclude.push('sortAscending', 'sortDescending', 'sortUnSort');
        }
        params.defaultItems.forEach((item: MenuItemDef | DefaultMenuItem) => {
          const name = nameOf(item);
          if (name != null && itemsToExclude.indexOf(name as DefaultMenuItem) < 0) {
            byName[name] = item;
          }
        });
        // Ensure sort and pin are available when column is sortable (AG Grid may omit them in some contexts).
        if (colDef?.sortable !== false) {
          if (byName['sortAscending'] == null) byName['sortAscending'] = 'sortAscending';
          if (byName['sortDescending'] == null) byName['sortDescending'] = 'sortDescending';
          if (byName['sortUnSort'] == null) byName['sortUnSort'] = 'sortUnSort';
        }
        if (byName['pinSubMenu'] == null) byName['pinSubMenu'] = 'pinSubMenu';

        // Custom: distribute columns to fill available width (size to fit).
        const distributeColumnsItem: MenuItemDef = {
          name: 'Distribute Columns',
          action: () => {
            params.api?.sizeColumnsToFit?.();
          },
        };

        // Fixed order so every column menu is consistent: sort, then pin, then rest.
        // Also hiding the Column Chooser Menu option since we have this custom built. 
        // We should look at using what's included. 
        const preferredOrder: (DefaultMenuItem | 'distributeColumns')[] = [
          'sortAscending',
          'sortDescending',
          'sortUnSort',
          'separator',
          'pinSubMenu',
          'pinLeft',
          'pinRight',
          'pinRowSubMenu',
          'pinTop',
          'pinBottom',
          'unpinRow',
          'clearPinned',
          'separator',
          'resetColumns',
          'autoSizeThis',
          'autoSizeAll',
          'distributeColumns',
          'rowGroup',
          'rowUnGroup',
          'expandAll',
          'contractAll',
          'valueAggSubMenu',
          'columnFilter',
          'separator',
          'copy',
          'copyWithHeaders',
          'copyWithGroupHeaders',
          'cut',
          'paste',
          'export',
          'csvExport',
          'excelExport',
        ];
        const ordered: (MenuItemDef | DefaultMenuItem)[] = [];
        const added: Record<string, boolean> = {};
        preferredOrder.forEach((name) => {
          if (name === 'distributeColumns') {
            ordered.push(distributeColumnsItem);
            return;
          }
          const item = byName[name];
          if (item != null) {
            ordered.push(item);
            added[name] = true;
          }
        });
        Object.keys(byName).forEach((name) => {
          if (!added[name]) ordered.push(byName[name]);
        });
        // Sanitize: if any MenuItemDef has subMenu as a key but the value
        // is not a real array, remove it. This prevents AG Grid from
        // iterating undefined AND prevents empty submenu arrows.
        const sanitized = ordered.map((item) => {
          if (typeof item === 'string' || item == null) {
            return item;
          }
          const def = { ...(item as MenuItemDef) };
          if ('subMenu' in def) {
            if (!Array.isArray(def.subMenu) || def.subMenu.length === 0) {
              delete def.subMenu;
            }
          }
          return def;
        });
        // Drop separators that have nothing following them (no non-separator item after).
        const isSeparator = (x: MenuItemDef | DefaultMenuItem): boolean =>
          x === 'separator' || (typeof x === 'object' && x != null && (x as MenuItemDef).name === 'separator');
        return sanitized.filter((item, index) => {
          if (!isSeparator(item)) return true;
          for (let i = index + 1; i < sanitized.length; i++) {
            if (!isSeparator(sanitized[i])) return true;
          }
          return false;
        });
      },
    },
    'shallow'
  );

  // Don't let it load modules twice
  window.__CMX_GRID_INITIALIZED__ = true;
}

/** Row/cell spacing preset for a single grid instance (`default` = `:root` semantic tokens). */
export type CmxGridDensity = 'default' | 'compact' | 'sparse';

export type ReactGridProps = AgGridReactProps & {
  density?: CmxGridDensity;
};

//Export the grid so we can use it in other places
export const ReactGrid = React.forwardRef<AgGridReact, ReactGridProps>((props, ref) => {
  const { className, density = 'default', gridOptions, ...agGridProps } = props;
  useEffect(() => {
    applyLicense();
  }, []);

  const wrapperClass = ['cmx-ag-grid-wrapper', className].filter(Boolean).join(' ');

  return (
    <div
      className={wrapperClass || undefined}
      data-cmx-grid-density={density === 'default' ? undefined : density}
      style={{ height: '100%', width: '100%', minHeight: 0 }}
    >
      <AgGridReact
        ref={ref}
        {...agGridProps}
        gridOptions={{
          ...gridOptions,
          theme: gridOptions?.theme ?? agGridMojaveLight,
          sideBar: false,
          suppressContextMenu: false, //This turns off the chart menu
          // Legacy column menu: omit "Choose Columns" tab; merge with consumer defaultColDef.
          defaultColDef: {
            ...gridOptions?.defaultColDef,
            menuTabs: ['generalMenuTab', 'filterMenuTab'],
          },
          // The custom theme gets placed in this layer so we can control order.
          // Will want this once we get spectre and other things cleaned out.
          //themeCssLayer: 'ag-grid-mojave-theme',
          // This loads the styles before the component.
          themeStyleContainer: document.body,
        }}
      />
    </div>
  );
});

ReactGrid.displayName = 'ReactGrid';

export { agGridMojaveLight, agGridMojaveDark } from './aggrid-theme';

// Export types (These remain the same in v33)
export type {
  CellClickedEvent,
  RowClickedEvent,
  RowSelectedEvent,
  SelectionChangedEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
  IHeaderParams,
  IServerSideGetRowsParams,
  ITooltipParams,
  ICellRendererParams,
  //TextFilterModule,
  //NumberFilterModule,
  //DateFilterModule,
  //SetFilterModule,
  //AdvancedFilterModule,
  //MultiFilterModule,
  ClientSideRowModelModule,
  RowSelectionModule
} from 'ag-grid-enterprise';
